<template>
  <div ref="chatContent" class="chat-content">
    <ChatMessage
      v-for="message in messages"
      :key="message.id"
      :message="message"
      :messages="messages"
      :participants="participants"
      :last-reads="lastReads"
      :locale="locale"
      :tab-visible="tabVisible"
      :can-edit="canEdit"
      :current-chat-participant="currentChatParticipant"
      :current-chat-user="currentChatUser"
      @read="$emit('read', message.id)"
      @delete="$emit('delete', message.id)"
      @reply="$emit('reply', message)"
    />
    <ChatTypingStatus
      v-if="typing.length"
      :current-chat-user="currentChatUser"
      :typing="typing"
      :conversation="conversation"
    />
  </div>
</template>

<script lang="ts">
import type { ComputedRef, PropType } from 'vue';
import { nextTick, defineComponent, ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import type { Participant } from '../../types';
import type {
  ChatParticipant,
  ChatParticipantId,
  Conversation,
  LastReadBy,
  Message,
  Typing,
} from '../types';
import ChatMessage from './ChatMessage.vue';
import ChatTypingStatus from './ChatTypingStatus.vue';

export default defineComponent({
  name: 'ChatContent',
  components: {
    ChatMessage,
    ChatTypingStatus,
  },
  props: {
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    conversation: {
      type: Object as PropType<Conversation>,
      default: undefined,
    },
    currentChatParticipant: {
      type: Object as PropType<ChatParticipant>,
      default: undefined,
    },
    lastReads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
    typing: {
      type: Array as PropType<Array<Typing>>,
      default: () => [],
    },
    locale: {
      type: String,
      default: '',
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: () =>
        ({
          participantId: '',
          userId: '',
        }) as ChatParticipantId,
    },
  },
  emits: ['read', 'reply', 'delete'],
  setup(props) {
    const chatContent = ref(null);

    const scrollToBottom = async (smooth = false): Promise<void> => {
      if (!chatContent.value) {
        return;
      }
      await nextTick();
      chatContent.value?.scrollTo({
        top: chatContent.value.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    };
    const messages: ComputedRef<Message[]> = computed(() => props.conversation?.messages || []);

    watch(
      () => [props.conversation?.messages.length, props.conversation?.id],
      ([newLength, newId], [oldLength, oldId]) => {
        if (newLength !== oldLength && newId === oldId) {
          scrollToBottom(true);
        }

        if (newId !== oldId) {
          scrollToBottom(false);
        }
      }
    );

    const tabVisible = ref(true);

    const onBlur = () => {
      tabVisible.value = false;
    };

    const onFocus = () => {
      tabVisible.value = true;
    };

    onMounted(async () => {
      window.addEventListener('blur', onBlur);
      window.addEventListener('focus', onFocus);
      scrollToBottom(false);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    });

    return {
      chatContent,
      scrollToBottom,
      tabVisible,
      messages,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared/src/scss/blink';

.chat-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  overflow: auto;
  background-color: var(--white);

  .message-container:first-child {
    margin-top: auto;
  }

  .message-container:last-child {
    padding-bottom: var(--spacing-50);
  }
}
</style>
